import { ILocation } from "./models/ILocation";
import { FEET_PER_MILE, metresToFeet } from "./Conversion";
import { Kernel } from "./Kernel";
import { celsiusToFahrenheit } from "./Conversion";
import { IPlace } from "../main";
import { getName } from "i18n-iso-countries";
import { DisplayUnits } from "../models";
import { DateTime } from "luxon";

export function getCountryName(countryCode: string) {
    return getName(countryCode, "en", { select: "alias" });
}

export function formatPlaceAddress(place: IPlace | undefined): string {
    if (place) {
        const addr = place.data.address;
        if (addr) {
            const country = addr.country ? getCountryName(addr.country) : undefined;
            const parts: string[] = [];
            parts.push(`${addr?.streetNumber ?? ""} ${addr?.street ?? ""}`);
            parts.push(addr?.suburb ?? "");
            parts.push(`${addr?.state ?? ""} ${addr?.postcode ?? ""}`);
            parts.push(country ?? "");
            return parts.filter((x) => x.trim().length > 0).join(", ");
        }
    }
    return ``;
}

export function formatLocation(loc: ILocation | undefined) {
    if (loc) {
        const dp = 5;
        return `${loc.lat.toFixed(dp)}, ${loc.lon.toFixed(dp)}`;
    }
    return "";
}

export function formatDistance(distance: number, decimals = 0, autoUnits = false) {
    if (distance === undefined || distance === null) {
        return ``;
    }
    const units = Kernel.instance().currentUserSettings.get().displayUnits;
    const feet = metresToFeet(distance);
    let imperial: string | undefined;
    let metric: string | undefined;

    if (units === DisplayUnits.METRIC || units === DisplayUnits.BOTH) {
        if (autoUnits && distance > 1000) {
            metric = `${(distance / 1000).toFixed(1)}km`;
        }
        if (!metric) {
            metric = `${distance.toFixed(decimals)}m`;
        }
    }

    if (units === DisplayUnits.IMPERIAL || units === DisplayUnits.BOTH) {
        if (autoUnits && feet > FEET_PER_MILE / 4) {
            imperial = `${(feet / FEET_PER_MILE).toFixed(1)}m`;
        }
        if (!imperial) {
            imperial = `${feet.toFixed(decimals)}ft`;
        }
    }

    switch (units) {
        case DisplayUnits.BOTH:
            return `${metric} (${imperial})`;
        case DisplayUnits.METRIC:
            return metric;
        case DisplayUnits.IMPERIAL:
            return imperial;
    }

    return "";
}

export function formatTemperature(celsius: number, decimals = 0) {
    const units = Kernel.instance().currentUserSettings.get().displayUnits;
    let imperial: string | undefined;
    let metric: string | undefined;

    if (units === DisplayUnits.METRIC || units === DisplayUnits.BOTH) {
        metric = `${celsius.toFixed(decimals)}°C`;
    }

    if (units === DisplayUnits.IMPERIAL || units === DisplayUnits.BOTH) {
        imperial = `${celsiusToFahrenheit(celsius).toFixed(decimals)}°F`;
    }

    switch (units) {
        case DisplayUnits.BOTH:
            return `${metric} (${imperial})`;
        case DisplayUnits.METRIC:
            return metric;
        case DisplayUnits.IMPERIAL:
            return imperial;
    }

    return "";
}

function toDateTime(date?: string | DateTime) {
    if (!date) {
        date = DateTime.utc();
    } else if (typeof date === "string") {
        date = DateTime.fromISO(date);
    }

    return date;
}

export function formatDateTime(date?: string | DateTime) {
    return toDateTime(date).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY);
}

export function formatDate(date?: string | DateTime) {
    return toDateTime(date).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
}
